import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { FileText } from 'lucide-react';
import { toast } from 'sonner';

function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function getMonthOptions() {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('bg-BG', { year: 'numeric', month: 'long' });
    months.push({ value, label });
  }
  return months;
}

export const SummaryTab = () => {
  const [summary, setSummary] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());

  useEffect(() => { loadSummary(); }, [selectedMonth]);

  const loadSummary = async () => {
    try {
      const response = await axios.get(`${API}/production-entries/summary?month=${selectedMonth}`);
      setSummary(response.data);
    } catch {
      console.error('Error loading summary');
    }
  };

  const handleExportSalaryPDF = async () => {
    try {
      const response = await axios.get(`${API}/export/salary-pdf?month=${selectedMonth}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `salary_report_${selectedMonth}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF файлът е изтеглен успешно');
    } catch {
      toast.error('Грешка при изтегляне на PDF');
    }
  };

  return (
    <Card className="p-6 mb-6">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div className="flex items-center gap-4">
          <Label className="text-sm uppercase tracking-wide font-medium">Месец:</Label>
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              {getMonthOptions().map((m) => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={handleExportSalaryPDF} className="bg-red-600 hover:bg-red-700 font-bold uppercase">
          <FileText className="w-5 h-5 mr-2" />Изтегли заплати PDF
        </Button>
      </div>

      <h2 className="heading-font text-xl font-bold uppercase mb-4">Обобщение по служители</h2>

      {summary && summary.employees && summary.employees.length > 0 ? (
        <div className="space-y-4">
          {summary.employees.map((emp) => (
            <Card key={emp.employee_id} className="p-4 bg-slate-50">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-bold text-lg">{emp.employee_name}</h3>
                  <p className="text-sm text-slate-600 mono-font">{emp.employee_number}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-600">{emp.total_earnings.toFixed(2)} EUR</p>
                  <p className="text-xs text-slate-500">Основна: {emp.base_earnings.toFixed(2)} + Доп: {emp.additional_payment.toFixed(2)} EUR</p>
                </div>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 text-center border-t pt-3">
                <div><p className="text-xs text-slate-500 uppercase">Бройки</p><p className="font-bold text-lg">{emp.total_quantity}</p></div>
                <div><p className="text-xs text-slate-500 uppercase">Брак</p><p className="font-bold text-lg text-red-600">{emp.total_defects}</p></div>
                <div><p className="text-xs text-slate-500 uppercase">Минути</p><p className="font-bold text-lg text-blue-600">{Math.round(emp.total_minutes * 100) / 100}</p></div>
                <div><p className="text-xs text-slate-500 uppercase">Цена/мин</p><p className="font-bold text-lg">{emp.price_per_minute} EUR</p></div>
                <div><p className="text-xs text-slate-500 uppercase">Доплащане</p><p className="font-bold text-lg text-amber-600">{emp.additional_payment.toFixed(2)} EUR</p></div>
              </div>
              {emp.by_operation && emp.by_operation.length > 0 && null}
            </Card>
          ))}
          <Card className="p-4 bg-amber-50 border-amber-200">
            <div className="flex justify-between items-center">
              <span className="font-bold text-lg uppercase">Общо за месеца:</span>
              <span className="text-3xl font-bold text-green-600">
                {summary.employees.reduce((sum, e) => sum + e.total_earnings, 0).toFixed(2)} EUR
              </span>
            </div>
          </Card>
        </div>
      ) : (
        <div className="text-center py-12 text-slate-500">
          <p>Няма данни за избрания месец.</p>
          <p className="text-sm mt-2">Уверете се, че сте задали цени за работниците в таб "Цени".</p>
        </div>
      )}
    </Card>
  );
};
